import styled from '@emotion/styled';
import { Col, Input } from 'antd';
import React from 'react';
import { connect } from 'react-redux';

import { AppState } from 'src/config/appState';
import { StepButtons } from 'src/modules/programs/components/StepButtons';
import * as S from 'src/modules/programs/stepComponents/styles';
import { color, font } from 'src/shared/styles/variables';

import {
    changeLivingAccordingToValuesFeelings,
    submitLivingValuesDataRequest,
} from '../actions/weekThreeActions';
import { LivingAccordingToValues } from '../models/weekThree';
import {
    areLivingValuesValid,
    getLivingAccordingToValuesFeelings,
} from '../selectors/weekThreeSelectors';

const FeelingBoxWrapper = styled(Col)({
    padding: '0.88rem',
});

const FeelingBox = styled('div')({
    backgroundColor: color.white,
    borderRadius: '0.44rem',
    padding: '1.09rem 1.31rem',
});

const Heading = styled('h3')(
    {
        color: color.chathamsBlue,
        margin: '2.19rem 0rem 0.88rem',
    },
    font.heavy(font.size.mediumExtended),
);

const TextArea = styled(Input.TextArea)({
    '&&.ant-input': {
        maxWidth: '52rem',
        marginTop: '0.3rem',
        margin: '0.5rem 0 1rem',
        padding: '0.8rem 1.1rem',
        resize: 'none',
        borderRadius: '0.3rem',
        border: `solid 1px ${color.paleSlate}`,
    },
});

const HighlightedText = styled('span')({
    color: color.bittersweet,
});

interface StateProps {
    data: LivingAccordingToValues;
    areLivingValuesValid: boolean;
}

interface ActionsProps {
    changeLivingAccordingToValuesFeelings: typeof changeLivingAccordingToValuesFeelings;

    submitLivingValuesDataRequest: typeof submitLivingValuesDataRequest;
}

interface Props extends StateProps, ActionsProps {
    goToPreviousStep: () => void;
    goToNextStep: () => void;
}

export class LivingAccordingToYourValuesStepContainer extends React.Component<Props> {
    private changeLivingAccordingToValuesFeelings = (value: string, field: string) =>
        this.props.changeLivingAccordingToValuesFeelings({ field, value });

    private handleLiveWithValuesChange = ({ target }: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.changeLivingAccordingToValuesFeelings(target.value, 'livingWithValuesFeelings');
    };

    private handleLiveAgainstValuesChange = ({
        target,
    }: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.changeLivingAccordingToValuesFeelings(target.value, 'livingAgainstValuesFeelings');
    };

    private handleSubmit = () => {
        if (!this.props.areLivingValuesValid) {
            return;
        }

        this.props.submitLivingValuesDataRequest(this.props.data);
        this.props.goToNextStep();
    };

    public render(): JSX.Element {
        const { data } = this.props;

        return (
            <>
                <S.ContentRow>
                    <S.ContentColumn>
                        <S.StepDescription small>Practice:</S.StepDescription>
                        <S.StepHeading>Living According to Your Values</S.StepHeading>
                        <S.StepDescription small>
                            It can be helpful to pay attention to what we are doing when we feel
                            like we ARE and ARE NOT living according to our values so that we know
                            what we should do more or less of.
                        </S.StepDescription>
                    </S.ContentColumn>
                </S.ContentRow>
                <S.ContentRow type="flex">
                    <FeelingBoxWrapper md={12}>
                        <FeelingBox>
                            <Heading>
                                What were you doing when you
                                <HighlightedText> were </HighlightedText>
                                living according to your values? How did you feel doing it?
                            </Heading>
                            <TextArea
                                rows={12}
                                placeholder="E.g., I was taking a walk with a friend and felt great "
                                onChange={this.handleLiveWithValuesChange}
                                value={data.livingWithValuesFeelings}
                            />
                        </FeelingBox>
                    </FeelingBoxWrapper>
                    <FeelingBoxWrapper md={12}>
                        <FeelingBox>
                            <Heading>
                                What were you doing when you
                                <HighlightedText> weren't </HighlightedText>
                                living according to your values? How did you feel doing it?
                            </Heading>
                            <TextArea
                                rows={12}
                                placeholder="E.g., I was watching TV and felt bored"
                                onChange={this.handleLiveAgainstValuesChange}
                                value={data.livingAgainstValuesFeelings}
                            />
                        </FeelingBox>
                    </FeelingBoxWrapper>
                </S.ContentRow>
                <StepButtons
                    onPreviousClick={this.props.goToPreviousStep}
                    onNextClick={this.handleSubmit}
                    nextDisabled={!this.props.areLivingValuesValid}
                    hasNextStep
                />
            </>
        );
    }
}

const mapStateToProps = (state: AppState): StateProps => ({
    data: getLivingAccordingToValuesFeelings(state),
    areLivingValuesValid: areLivingValuesValid(state),
});

export const LivingAccordingToYourValuesStep = connect<StateProps, ActionsProps>(mapStateToProps, {
    changeLivingAccordingToValuesFeelings,
    submitLivingValuesDataRequest,
})(LivingAccordingToYourValuesStepContainer);
